
import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/components/ui/drawer";
import { format } from 'date-fns';
import { Database } from '@/integrations/supabase/types';
import { CalendarDays, Clock, MapPin, X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { EventImageCarousel } from "@/components/event-details/EventImageCarousel";
import { EventImageWithCaption } from "@/utils/eventImageUtils";

type TimeOfDay = Database["public"]["Enums"]["time_of_day"];

interface Event {
  name: string;
  description: string;
  event_date: string;
  image_url?: string;
  status: string;
  location_type: string;
  flexible_location_description: string;
  preferred_dates: string[];
  preferred_venues: any[];
  preferred_time_of_day: TimeOfDay[];
  host: {
    full_name: string;
  };
  event_id: string;
  event_deadline?: string;
  event_images?: {
    image_id: string;
    image_url: string;
    is_primary: boolean;
    display_order: number;
  }[];
}

interface EventDetailsBottomDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event;
}

export const EventDetailsBottomDrawer = ({
  open,
  onOpenChange,
  event
}: EventDetailsBottomDrawerProps) => {
  // Format date display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEE, MMM d');
    } catch (error) {
      return 'Date TBD';
    }
  };

  // Generate appropriate time of day display
  const formatTimeOfDay = (times: TimeOfDay[]) => {
    if (!times || times.length === 0) return 'Time TBD';
    
    return times.map(time => 
      time.charAt(0).toUpperCase() + time.slice(1)
    ).join(', ');
  };

  // Prepare the images for the carousel
  const imageUrls = event.event_images || [];
  const hasImages = imageUrls.length > 0 || event.image_url;

  console.log("Event images:", event.event_images);
  console.log("Event image_url:", event.image_url);
  console.log("Has images:", hasImages);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader className="text-left">
            <DrawerTitle>{event.name}</DrawerTitle>
            <DrawerDescription>
              Hosted by {event.host.full_name}
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-4 pb-8">
            {/* Add Event Image Carousel */}
            {hasImages && (
              <div className="mb-6 -mx-4">
                <EventImageCarousel 
                  images={imageUrls}
                  fallbackImage={event.image_url} 
                  eventTitle={event.name}
                  aspectRatio={16/9}
                />
              </div>
            )}

            {event.description && (
              <div className="mb-6 text-sm">
                {event.description}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-start">
                <CalendarDays className="h-5 w-5 mr-3 text-muted-foreground" />
                <div>
                  <p className="font-medium">Date</p>
                  {event.preferred_dates.length > 0 ? (
                    <ul className="text-sm text-muted-foreground">
                      {event.preferred_dates.map((date, index) => (
                        <li key={index}>{formatDate(date)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">Date TBD</p>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <Clock className="h-5 w-5 mr-3 text-muted-foreground" />
                <div>
                  <p className="font-medium">Time</p>
                  <p className="text-sm text-muted-foreground">
                    {formatTimeOfDay(event.preferred_time_of_day)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <MapPin className="h-5 w-5 mr-3 text-muted-foreground" />
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-sm text-muted-foreground">
                    {event.flexible_location_description || 'Location TBD'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
